import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Calendar, Users, FileText, Bell, ArrowRight, TrendingUp } from 'lucide-react';
import apiClient from '../services/apiClient';
import { adminEventService } from '../services/adminEventService';
import { useAdminAuth } from '../hooks/useAdminAuth';

interface Stats {
  events: number;
  sessions: number;
  attendees: number;
  papers: number;
}

function StatCard({ label, value, icon: Icon, color }: {
  label: string;
  value: number | string;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-gray-400">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon size={15} />
        </div>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

export default function DashboardPage() {
  const { activeWorkspaceId } = useAdminAuth();
  const [stats, setStats] = useState<Stats>({ events: 0, sessions: 0, attendees: 0, papers: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      try {
        const allEvents = activeWorkspaceId ? await adminEventService.list(activeWorkspaceId) : await apiClient.get<{ id: string }[]>('/events');
        let sessions = 0, attendees = 0, papers = 0;
        if (allEvents.length > 0) {
          const analyticsResults = await Promise.all(
            allEvents.map((e) => apiClient.get<{ sessions: number; attendees: number; papers: number }>(`/admin/analytics?event_id=${e.id}`).catch(() => ({ sessions: 0, attendees: 0, papers: 0 })))
          );
          sessions = analyticsResults.reduce((s, r) => s + (r.sessions ?? 0), 0);
          attendees = analyticsResults.reduce((s, r) => s + (r.attendees ?? 0), 0);
          papers = analyticsResults.reduce((s, r) => s + (r.papers ?? 0), 0);
        }
        setStats({ events: allEvents.length, sessions, attendees, papers });
      } finally {
        setLoading(false);
      }
    }
    void fetchStats();
  }, [activeWorkspaceId]);

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400 text-sm mt-1">Welcome back — here's what's happening.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Events" value={loading ? '—' : stats.events} icon={Calendar} color="bg-sky-500/10 text-sky-400" />
        <StatCard label="Sessions" value={loading ? '—' : stats.sessions} icon={TrendingUp} color="bg-violet-500/10 text-violet-400" />
        <StatCard label="Attendees" value={loading ? '—' : stats.attendees} icon={Users} color="bg-emerald-500/10 text-emerald-400" />
        <StatCard label="Papers" value={loading ? '—' : stats.papers} icon={FileText} color="bg-amber-500/10 text-amber-400" />
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { label: 'Manage Events', desc: 'Create or edit conference events', href: '/events', icon: Calendar, color: 'text-sky-400' },
            { label: 'Send Notification', desc: 'Broadcast a message to attendees', href: '/notifications', icon: Bell, color: 'text-violet-400' },
            { label: 'View Analytics', desc: 'Engagement and attendance metrics', href: '/analytics', icon: TrendingUp, color: 'text-emerald-400' },
          ].map(({ label, desc, href, icon: Icon, color }) => (
            <Link
              key={href}
              to={href}
              className="flex items-start gap-4 p-4 bg-gray-900 border border-gray-800 rounded-xl hover:border-gray-600 transition-colors group"
            >
              <div className={`mt-0.5 ${color}`}><Icon size={18} /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white">{label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
              </div>
              <ArrowRight size={14} className="mt-1 text-gray-600 group-hover:text-gray-400 transition-colors" />
            </Link>
          ))}
        </div>
      </div>

      {/* Setup reminder */}
      <div className="bg-sky-500/5 border border-sky-500/20 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-sky-400 mb-1">🚀 Getting Started</h3>
        <p className="text-sm text-gray-400">
          Create your first event, add sessions and speakers, then use Papers → Embed All Content to power the AI Copilot.
        </p>
      </div>
    </div>
  );
}
